// Libraries
import { useEffect, useState, useRef } from "react";
import { MapContainer, GeoJSON, FeatureGroup, Circle, Pane, ZoomControl, ScaleControl } from "react-leaflet";
import { PathOptions, geoJson} from 'leaflet';

// Components
import { mouseOverTextVD, mouseOverTextState, mouseOverTextTract, mouseOverTextPoll, 
         mouseOverTextPollSummary, pointerOver, pointerOut } from "./Tooltip";
import { BackgroundPane, TilePane } from "./Panes";
import FullScreen from "./Controls";
import { handleClickState, handleClickCounty } from "../utils/AnalyticsTracking";

// Types
import { State, PollingLoc, ChangeYear, EquityIndicator, Indicator } from "../utils/Types";

// Global
import { defaultMap, defaultCounty  } from "../utils/Global";
import { useStableCallback, returnCountyShouldInteract, filterGeoByBounds, 
         filterPointByBounds, partOfSelectedState, partOfSelectedCounty } from "../utils/Helper";
import { filterSelected } from "./Filters";

// Styles
import { layersStyle, highlightGeographicBoundary, vdStyle, choroplethStyle, pollStyle, pollSummarySize, highlightInnerGeography } from "../utils/Theme";

function LayersComponent({ mapRef, geoFocus, setGeoFocus, selectedState, setSelectedState, selectedCounty, setSelectedCounty, 
                           showPolls, setPollHover, showVD, showCensusTracts, showEquityIndicator, changeYear, equityIndicator, setGeoHover, 
                           pollingLocsData, statesData, countiesData, tractsData, vdData, loadedCountiesData, loadedTractsData, loadedVdData, loadedStatesData,
                           loadedPollingLocsData, indicator,  
                           pctBlackCVAPCountyFilter, pctHispanicCVAPCountyFilter, popDensityCountyFilter, 
                           pctBlackCVAPTractFilter, pctHispanicCVAPTractFilter, popDensityTractFilter,
                           changeNoPollsCountyFilter, overallChangeCountyFilter,
                           changeYearCVAPPerPollCountyFilter, baseYearCVAPPerPollCountyFilter,
                           changeNoPollsTractFilter, overallChangeTractFilter,
                           setVisualizationLoading }: 
                        {  mapRef: any, geoFocus: string, setGeoFocus: Function,
                           selectedState: State, setSelectedState: any, 
                           selectedCounty: any, setSelectedCounty: any, 
                           showPolls: boolean,  setPollHover: any, showVD: boolean, 
                           showCensusTracts: boolean, showEquityIndicator: boolean,
                           changeYear: ChangeYear, equityIndicator: EquityIndicator, setGeoHover: any, 
                           pollingLocsData: any, statesData: GeoJSON.FeatureCollection, countiesData: GeoJSON.FeatureCollection, tractsData: GeoJSON.FeatureCollection,
                           vdData: GeoJSON.FeatureCollection,
                           loadedCountiesData: boolean, loadedTractsData: boolean, loadedVdData: boolean, loadedPollingLocsData: boolean, loadedStatesData: boolean,
                           indicator: Indicator, 
                           pctBlackCVAPCountyFilter: number[], pctHispanicCVAPCountyFilter: number[], popDensityCountyFilter: number[],
                           pctBlackCVAPTractFilter: number[], pctHispanicCVAPTractFilter: number[], popDensityTractFilter: number[],
                           changeNoPollsCountyFilter: number[], overallChangeCountyFilter: number[],
                           changeYearCVAPPerPollCountyFilter: number[], baseYearCVAPPerPollCountyFilter: number[],
                           changeNoPollsTractFilter: number[], overallChangeTractFilter: number[],
                           setVisualizationLoading: Function }) {

    const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);
    const [geoJsonBoundaryData, setGeoJsonBoundaryData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);
    const [geoJsonVdData, setGeoJsonVdData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);

    const [loadedGeoJsonData, setLoadedGeoJsonData] = useState<boolean>(false);
    const [loadedGeoJsonBoundaryData, setLoadedGeoJsonBoundaryData] = useState<boolean>(false);

    const [pollingLocsInBound, setPollingLocsInBound] = useState<any[]>([]);

    const [focusedCounties, setFocusedCounties] = useState<string[]>([]);
    const [focusedTracts, setFocusedTracts] = useState<string[]>([]);

    const geoJsonRef = useRef<L.GeoJSON<any, any>>(null);
    const geoJsonBoundaryRef = useRef<L.GeoJSON<any, any>>(null);
    const geoJsonVdRef = useRef<L.GeoJSON<any, any>>(null);

    // Functions ---------------------------------------------------

    const stableMouseoutCallback = useStableCallback(mouseOut);
    const stableMouseoutBoundaryCallback = useStableCallback(mouseOutBoundary);
    const stableMouseoverCallback = useStableCallback(mouseOver);
    const stableOnClickCallback = useStableCallback(onClickFeature);
    const stableMouseoverBoundaryCallback = useStableCallback(mouseOverBoundary);
    const stableMouseoverPollSummaryCallback = useStableCallback(mouseOverPollSummary);

    function mouseOver(properties: any) {

        if (properties.type === "State") {
            return mouseOverState;
        } else if(properties.type === "County") {
            return mouseOverCounty;
        } else if(properties.type === "Tract") {
            return mouseOverTract;
        }
    }

    function mouseOverBoundary(properties: any) {

        function defaultMouseOver() {
            return <></>;
        }

        if (!showCensusTracts && properties.type === "County") {
            return mouseOverCounty;
        } else {
            return defaultMouseOver;
        }
    }

    function mouseOverPollingLoc(d: any) {
        var coords = mapRef.current.latLngToContainerPoint(d.latlng);
        pointerOver(coords.x + 20, coords.y - 10, mouseOverTextPoll(d), false);
        setPollHover(d);
    }

    function mouseOverVD(event: any) {
        var layer = event.target;
        layer.setStyle(layersStyle.VD.highlight);
        var coords = mapRef.current.latLngToContainerPoint(layer.feature.properties.latlng);
        pointerOver(coords.x + 20, coords.y - 10, mouseOverTextVD(layer.feature), false);
    }

    function mouseOverTract(event: any) {
        var layer = event.target;
        layer.setStyle(highlightInnerGeography(layer.feature));
        var coords = mapRef.current.latLngToContainerPoint(layer.feature.properties.latlng);
        pointerOver(coords.x, coords.y, mouseOverTextTract(layer.feature, equityIndicator, changeYear, showEquityIndicator), false);
        setGeoHover(layer.feature.properties);
    }

    function mouseOverCountyorPollSummary(feature: any) {
        if (!feature.properties.changeYearData.pollSummary.missingDataFlag && feature.properties.selected) {
            setPollHover(feature.properties);
            setGeoHover(feature.properties);
        }
        var coords = mapRef.current.latLngToContainerPoint(feature.properties.latlng);
        pointerOver(coords.x + 30, coords.y - 10, mouseOverTextPollSummary(feature, equityIndicator, changeYear, showEquityIndicator, indicator), !feature.properties.selected);
    }

    function mouseOverCounty(event: any) {
        var layer = event.target;
        if (returnCountyShouldInteract(changeYear, layer.feature.properties)) {
            if (layer.feature.properties.changeYearData !== undefined) {
                if (!layer.feature.properties.changeYearData.pollSummary.missingDataFlag && layer.feature.properties.selected) {
                    layer.setStyle(highlightInnerGeography(layer.feature));
                }
                mouseOverCountyorPollSummary(layer.feature);
            }
        }
    }

    function mouseOverPollSummary(feature: any) {
        mouseOverCountyorPollSummary(feature);
    }

    function mouseOverState(event: any) {
        var layer = event.target;
        layer.setStyle(layersStyle.State.highlight);
        var coords = mapRef.current.latLngToContainerPoint(layer.feature.properties.latlng);
        pointerOver(coords.x, coords.y, mouseOverTextState(layer.feature), false);
    }

    function mouseOutPoll() {
        pointerOut();
        setPollHover({});
    }

    function mouseOut(event: any) {
        var layer = event.target;
        layer.setStyle(choroplethStyle(layer.feature, equityIndicator, showEquityIndicator) as PathOptions);
        pointerOut();
        setGeoHover({});
        setPollHover({});
    }

    function mouseOutBoundary(event: any) {
        var layer = event.target;
        layer.setStyle(highlightGeographicBoundary(layer.feature, equityIndicator, showEquityIndicator, showCensusTracts) as PathOptions);
        pointerOut();
        setGeoHover({});
    }

    function onEachFeature(_: any, layer: any) {

        layer.on({
          mouseover: stableMouseoverCallback(layer.feature.properties),
          mouseout: stableMouseoutCallback,
          click: stableOnClickCallback
        });
        pointerOut();
    }

    function onEachBoundary(_: any, layer: any) {

        layer.on({
            mouseover: stableMouseoverBoundaryCallback(layer.feature.properties),
            mouseout: stableMouseoutBoundaryCallback
            });
        pointerOut();
    }

    function onEachVD(_: any, layer: any) {

        layer.on({
          mouseover: mouseOverVD,
          mouseout: stableMouseoutCallback
        });
        pointerOut();
    }

    function onClickFeature(event: any) {

        let properties = event.target.feature.properties;
        if (properties.type === "State") {
            setSelectedState(properties);
            handleClickState(properties.name);
            setSelectedCounty(defaultCounty);
            setGeoFocus('State')
        } else if ((properties.type === "County" && returnCountyShouldInteract(changeYear, properties))) {

            if (properties.changeYearData !== undefined) {
                if (!properties.changeYearData.pollSummary.missingDataFlag && properties.selected) {
                    setSelectedCounty(properties);
                    handleClickCounty(properties.cntyfp);
                    setGeoFocus('County');
                }
            }
        }
    }

    // React Hooks ---------------------------------------------------

    let featuresData: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: [] as GeoJSON.Feature[]
    };

    let boundaryData: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: [] as GeoJSON.Feature[]
    };

    // This hook updates the main geography and boundary data based on the selected geography
    useEffect(() => {
        // United States
        if (geoFocus === 'US') {
            if (loadedStatesData) {

                featuresData.features = statesData.features.map((d: GeoJSON.Feature) => {
                                    return { ...d, properties: { ...d.properties, selected: true } };
                                });

                setGeoJsonData(featuresData);
                setLoadedGeoJsonData(true);
                setLoadedGeoJsonBoundaryData(false);

                mapRef.current.flyTo(defaultMap.latlng, defaultMap.zoom) // zooms to country level, otherwise react finds the center of the world map in Africa
                    .on('moveend', () => {
                        setGeoJsonData(featuresData);
                        setLoadedGeoJsonData(true);
                        setLoadedGeoJsonBoundaryData(false);
                    });
                }

        // Selected State
        } else if (geoFocus === 'State') {
            if (loadedCountiesData) {

                featuresData.features = partOfSelectedState(countiesData, selectedState.stfp);
                boundaryData.features = partOfSelectedState(statesData, selectedState.stfp);

                if (selectedState.stfp !== '') {
                    mapRef.current
                    .flyTo(selectedState.latlng, selectedState.zoom) // zooms to state level
                }

                mapRef.current
                    .on('moveend', () => {
                        setGeoJsonData(filterGeoByBounds(mapRef, featuresData));
                        setLoadedGeoJsonData(true);
                        setGeoJsonBoundaryData(filterGeoByBounds(mapRef, boundaryData));
                        setLoadedGeoJsonBoundaryData(true);
                    });
            }
        // Selected County
        } else {
            if (loadedCountiesData && loadedTractsData && loadedVdData && loadedPollingLocsData) {
          
                featuresData.features = partOfSelectedCounty(tractsData, selectedCounty.cntyfp);
                boundaryData.features = partOfSelectedCounty(countiesData, selectedCounty.cntyfp);

                let vdfc: GeoJSON.FeatureCollection = {
                    type: "FeatureCollection",
                    features: partOfSelectedCounty(vdData, selectedCounty.cntyfp)
                };

                if (selectedCounty.cntyfp !== '') {
                    mapRef.current
                     .flyTo(selectedCounty.latlng, selectedCounty.zoom) // zooms to county level
                }

                setGeoJsonData(filterGeoByBounds(mapRef, featuresData));
                setLoadedGeoJsonData(true);
                setGeoJsonBoundaryData(filterGeoByBounds(mapRef, boundaryData));
                setLoadedGeoJsonBoundaryData(true);
                setGeoJsonVdData(filterGeoByBounds(mapRef, vdfc));
                setPollingLocsInBound(filterPointByBounds(mapRef, pollingLocsData));

                mapRef.current
                    .on('moveend', () => {
                        setGeoJsonData(filterGeoByBounds(mapRef, featuresData));
                        setGeoJsonBoundaryData(filterGeoByBounds(mapRef, boundaryData));
                        setGeoJsonVdData(filterGeoByBounds(mapRef, vdfc));
                        setPollingLocsInBound(filterPointByBounds(mapRef, pollingLocsData));
                    })
                    .whenReady(() => {setVisualizationLoading(false);});
            }
        }

    }, [selectedCounty, selectedState, geoFocus, changeYear, loadedStatesData, loadedCountiesData, loadedTractsData, 
        loadedVdData, loadedPollingLocsData, countiesData, tractsData, pollingLocsData]);

    // This hook updates which counties are focused based on the selected state
    useEffect(() => {

        if (loadedCountiesData) {
            if (selectedState.stfp !== "") {
                setFocusedCounties([...new Set(countiesData.features
                    .filter((feature: any) => selectedState.stfp === feature.properties.stfp)
                    .map((feature: any) => feature.properties.cntyfp))]);
            } else {
                setFocusedCounties([...new Set(countiesData.features
                    .map((feature: any) => feature.properties.cntyfp))]);
            }
        }

    }, [loadedCountiesData, countiesData, selectedState]);

    // This hooks updates which tracts are focused based on the selected county
    useEffect(() => {

        if (loadedTractsData) {
            if (selectedCounty.cntyfp !== "") {
                setFocusedCounties([...new Set(tractsData.features
                    .filter((feature: any) => selectedCounty.cntyfp === feature.properties.cntyfp)
                    .map((feature: any) => feature.properties.tractfp))]);
            } else {
                setFocusedCounties([...new Set(tractsData.features
                    .map((feature: any) => feature.properties.tractfp))]);
            }
        }

    }, [loadedTractsData, tractsData, selectedCounty]);

    // This Hooks Deals with Setting the Opacity Filters
    // It updates the selected property in the geoJsonData which is then used to set a generalized opacity
    // across the geographies and points on the geographies
    useEffect(() => {

        if (loadedCountiesData && loadedTractsData) {

            let counties: string[] = tractsData.features
                .filter((feature: any) => selectedState.stfp === feature.properties.stfp && 
                    filterSelected(feature, "baseYearPctCVAPBlack", pctBlackCVAPTractFilter, selectedState) &&
                    filterSelected(feature, "baseYearPctCVAPHispanic", pctHispanicCVAPTractFilter, selectedState) &&
                    filterSelected(feature, "baseYearPopDensity", popDensityTractFilter, selectedState) &&
                    filterSelected(feature, "changeNoPolls", changeNoPollsTractFilter, selectedState, true) &&
                    filterSelected(feature, "overallChange", overallChangeTractFilter, selectedState, true)
                    )
                .map((feature: any) => feature.properties.cntyfp);
    
                let uniqueCounties: string[] = [...new Set(counties)];
                setFocusedCounties(uniqueCounties);

            if (geoFocus === "State") { 
    
                geoJsonData.features.forEach((feature: any) => {
                    feature.properties.selected = filterSelected(feature, "baseYearPctCVAPBlack", pctBlackCVAPCountyFilter, selectedState) && 
                                                    filterSelected(feature, "baseYearPctCVAPHispanic", pctHispanicCVAPCountyFilter, selectedState) &&
                                                    filterSelected(feature, "baseYearPopDensity", popDensityCountyFilter, selectedState) &&
                                                    filterSelected(feature, "changeNoPolls", changeNoPollsCountyFilter, selectedState, true) &&
                                                    filterSelected(feature, "overallChange", overallChangeCountyFilter, selectedState, true) &&
                                                    filterSelected(feature, "changeYearCVAPPerPoll", changeYearCVAPPerPollCountyFilter, selectedState, true) &&
                                                    filterSelected(feature, "baseYearCVAPPerPoll", baseYearCVAPPerPollCountyFilter, selectedState, true) &&
                                                    uniqueCounties.includes(feature.properties.cntyfp);
                });
                setGeoJsonData(geoJsonData);

            } else if (geoFocus === "County") {
                geoJsonData.features.forEach((feature: any) => {
                    feature.properties.selected = (selectedCounty.cntyfp !== "" ? selectedCounty.cntyfp === feature.properties.cntyfp : true ) &&
                                                    filterSelected(feature, "baseYearPctCVAPBlack", pctBlackCVAPTractFilter, selectedState) && 
                                                    filterSelected(feature, "baseYearPctCVAPHispanic", pctHispanicCVAPTractFilter, selectedState) &&
                                                    filterSelected(feature, "baseYearPopDensity", popDensityTractFilter, selectedState) &&
                                                    filterSelected(feature, "changeNoPolls", changeNoPollsTractFilter, selectedState, true) &&
                                                    filterSelected(feature, "overallChange", overallChangeTractFilter, selectedState, true);
                });
                setGeoJsonData(geoJsonData);

                let tracts: any[] = tractsData.features
                .filter((feature: any) => selectedState.stfp === feature.properties.stfp &&
                    filterSelected(feature, "baseYearPctCVAPBlack", pctBlackCVAPTractFilter, selectedState) &&
                    filterSelected(feature, "baseYearPctCVAPHispanic", pctHispanicCVAPTractFilter, selectedState) &&
                    filterSelected(feature, "baseYearPopDensity", popDensityTractFilter, selectedState) &&
                    filterSelected(feature, "changeNoPolls", changeNoPollsTractFilter, selectedState, true) &&
                    filterSelected(feature, "overallChange", overallChangeTractFilter, selectedState, true));

                if (selectedCounty.cntyfp !== "") {
                    tracts.filter((feature: any) => selectedCounty.cntyfp === feature.properties.cntyfp);
                }

                let uniqueTracts: string[] = [...new Set(tracts.map((feature: any) => feature.properties.tractfp))];
                setFocusedTracts(uniqueTracts);
            }
          
        }
    }, [loadedCountiesData, loadedTractsData, countiesData, tractsData, geoJsonData, geoFocus,
        pctBlackCVAPCountyFilter, pctHispanicCVAPCountyFilter, popDensityCountyFilter, 
        pctBlackCVAPTractFilter, pctHispanicCVAPTractFilter, popDensityTractFilter, 
        changeNoPollsCountyFilter, overallChangeCountyFilter,
        changeYearCVAPPerPollCountyFilter, baseYearCVAPPerPollCountyFilter,
        changeNoPollsTractFilter, overallChangeTractFilter,
        selectedState, selectedCounty]);

    // Updates main geography and main boundary
    useEffect(() => {
        geoJsonRef.current?.clearLayers()
                    .addData(geoJsonData)
                    .setStyle((feature) => choroplethStyle(feature, equityIndicator, showEquityIndicator) as PathOptions);
        geoJsonBoundaryRef.current?.clearLayers()
                    .addData(geoJsonBoundaryData)
                    .setStyle((feature) => highlightGeographicBoundary(feature, equityIndicator, showEquityIndicator, showCensusTracts) as PathOptions);

    }, [geoJsonBoundaryData, geoJsonData, equityIndicator, changeYear, showEquityIndicator, showCensusTracts, indicator, 
        pctBlackCVAPCountyFilter, pctHispanicCVAPCountyFilter, popDensityCountyFilter,
        pctBlackCVAPTractFilter, pctHispanicCVAPTractFilter, popDensityTractFilter, 
        changeNoPollsCountyFilter, overallChangeCountyFilter,
        changeYearCVAPPerPollCountyFilter, baseYearCVAPPerPollCountyFilter,
        changeNoPollsTractFilter, overallChangeTractFilter,
        selectedState]);

    // Updates the voting districts
    useEffect(() => {
        geoJsonVdRef.current?.clearLayers().addData(geoJsonVdData).setStyle(vdStyle);
    }, [geoJsonVdData]);

    return(
        <>
            <Pane name="geo-pane" style={{ zIndex: 100 }}>
                { loadedGeoJsonData ? geoFocus === "County" ? showCensusTracts ? <GeoJSON data={geoJsonData} style={layersStyle.default} onEachFeature={onEachFeature} ref={geoJsonRef} key="geoJsonAll"/>: null : <GeoJSON data={geoJsonData} style={layersStyle.default} onEachFeature={onEachFeature} ref={geoJsonRef} key="geoJsonAll"/>: null }
                { loadedGeoJsonBoundaryData ? <GeoJSON data={geoJsonBoundaryData} style={layersStyle.outline} onEachFeature={onEachBoundary} ref={geoJsonBoundaryRef} key="geoJsonBoundary"/>: <></> }
                { showVD &&  loadedVdData ? <GeoJSON data={geoJsonVdData} style={vdStyle} onEachFeature={onEachVD} ref={geoJsonVdRef} key="geoJsonVD"/> :<></> }
            </Pane>
            <Pane name="poll-pane" style={{ zIndex: 200 }}>

            { geoFocus === "State" && loadedGeoJsonData ? 
                <FeatureGroup key="pollChangeSummaryFeatureGroup">
                    {
                        geoJsonData.features.map((feature: any, i: number) => {

                            let selected = filterSelected(feature, "baseYearPctCVAPBlack", pctBlackCVAPCountyFilter, selectedState) &&
                                           filterSelected(feature, "baseYearPctCVAPHispanic", pctHispanicCVAPCountyFilter, selectedState) &&
                                           filterSelected(feature, "baseYearPopDensity", popDensityCountyFilter, selectedState) &&
                                           filterSelected(feature, "changeNoPolls", changeNoPollsCountyFilter, selectedState, true) &&
                                           filterSelected(feature, "overallChange", overallChangeCountyFilter, selectedState, true) &&
                                           filterSelected(feature, "changeYearCVAPPerPoll", changeYearCVAPPerPollCountyFilter, selectedState, true) &&
                                           filterSelected(feature, "baseYearCVAPPerPoll", baseYearCVAPPerPollCountyFilter, selectedState, true) &&
                                           focusedCounties.includes(feature.properties.cntyfp);

                            if (feature.properties.changeYearData !== undefined && !feature.properties.changeYearData.pollSummary.missingDataFlag) {
                                return (
                                    <Circle key={i} 
                                            center={[feature.properties.latlng.lat, feature.properties.latlng.lng]} 
                                            pathOptions={pollStyle(feature.properties.changeYearData.pollSummary.status, selected, indicator, showEquityIndicator) as PathOptions} 
                                            radius={pollSummarySize(feature.properties.changeYearData.pollSummary.status, indicator)} 
                                            // eventHandlers={{
                                            //     click: () => {
                                            //         stableOnClickCallback(feature);
                                            //     },
                                            //     mouseover: () => {
                                            //         stableMouseoverPollSummaryCallback(feature);
                                            //     },
                                            //     mouseout: () => {    
                                            //         mouseOutPoll();
                                            //     }
                                            // }}
                                    />
                                );
                            } else {
                                return null;
                            }
                        })
                    }
                </FeatureGroup> : null}
            { geoFocus === "County" && showPolls && loadedPollingLocsData?
                <FeatureGroup key="pollingLocFeatureGroup">
                    {pollingLocsInBound.map((d: PollingLoc, i: number) => {

                        let selected = focusedTracts.includes(d.tractfp) && focusedCounties.includes(d.cntyfp);

                        if (!d.missingDataFlag) {
                            return(
                                <Circle key={i} 
                                        center={[d.latlng.lat, d.latlng.lng]} 
                                        pathOptions={pollStyle(d, selected, indicator, showEquityIndicator) as PathOptions} 
                                        radius={200} 
                                        eventHandlers={{
                                            mouseover: () => {
                                                mouseOverPollingLoc(d);
                                            },
                                            mouseout: () => {    
                                                mouseOutPoll();
                                            }
                                        }}
                            />)
                            }
                        }
                    )}
                </FeatureGroup> : null}
            </Pane>
        </>
    )
}

export default function Map({ mapRef, geoFocus, setGeoFocus, selectedState, setSelectedState, selectedCounty, setSelectedCounty, showPolls, 
                              setPollHover, showVD, showCensusTracts, showEquityIndicator, 
                              changeYear, equityIndicator, setGeoHover, 
                              pollingLocsData, statesData, countiesData, tractsData, vdData, 
                              loadedCountiesData, loadedTractsData, loadedVdData, loadedPollingLocsData, loadedStatesData,
                              indicator, 
                              pctBlackCVAPCountyFilter, pctHispanicCVAPCountyFilter, popDensityCountyFilter, 
                              pctBlackCVAPTractFilter, pctHispanicCVAPTractFilter, popDensityTractFilter,
                              changeNoPollsCountyFilter, overallChangeCountyFilter,
                              changeYearCVAPPerPollCountyFilter, baseYearCVAPPerPollCountyFilter,
                              changeNoPollsTractFilter, overallChangeTractFilter,
                              setVisualizationLoading}: 
                            { mapRef: any, geoFocus: string, setGeoFocus: Function, selectedState: State, setSelectedState: any, 
                              selectedCounty: any, setSelectedCounty: any, 
                              showPolls: boolean, setPollHover: any, showVD: boolean, 
                              showCensusTracts: boolean, showEquityIndicator: boolean,
                              changeYear: ChangeYear, equityIndicator: EquityIndicator, 
                              setGeoHover: any, 
                              pollingLocsData: any, statesData: GeoJSON.FeatureCollection, countiesData: GeoJSON.FeatureCollection, tractsData: GeoJSON.FeatureCollection,
                              vdData: GeoJSON.FeatureCollection, loadedCountiesData: boolean, loadedTractsData: boolean, 
                              loadedVdData: boolean, loadedPollingLocsData: boolean, loadedStatesData: boolean,
                              indicator: Indicator, 
                              pctBlackCVAPCountyFilter: number[], pctHispanicCVAPCountyFilter: number[], popDensityCountyFilter: number[],
                              pctBlackCVAPTractFilter: number[], pctHispanicCVAPTractFilter: number[], popDensityTractFilter: number[],
                              changeNoPollsCountyFilter: number[], overallChangeCountyFilter: number[],
                              changeYearCVAPPerPollCountyFilter: number[], baseYearCVAPPerPollCountyFilter: number[],
                              changeNoPollsTractFilter: number[], overallChangeTractFilter: number[],
                              setVisualizationLoading: Function }): JSX.Element {

    return(
        <MapContainer
            className="home-map"
            center={[defaultMap.latlng.lat, defaultMap.latlng.lng]}
            zoom={defaultMap.zoom}
            minZoom={4}
            maxZoom={18}
            zoomControl={false}
            ref={mapRef}
            scrollWheelZoom={false}
            zoomSnap={.25}
            zoomDelta={.25}
            wheelPxPerZoomLevel={60}
            wheelDebounceTime={90}
            >
            <TilePane/>
            <BackgroundPane />
            <LayersComponent mapRef={mapRef} geoFocus={geoFocus} setGeoFocus={setGeoFocus}
                             selectedState={selectedState} setSelectedState={setSelectedState} 
                             selectedCounty={selectedCounty} setSelectedCounty={setSelectedCounty}
                             showPolls={showPolls} showVD={showVD} showEquityIndicator={showEquityIndicator}
                             showCensusTracts={showCensusTracts} setPollHover={setPollHover}
                             changeYear={changeYear} equityIndicator={equityIndicator} setGeoHover={setGeoHover} 
                             pollingLocsData={pollingLocsData} countiesData={countiesData} tractsData={tractsData}
                             vdData={vdData} statesData={statesData}
                             loadedCountiesData={loadedCountiesData} loadedTractsData={loadedTractsData} loadedVdData={loadedVdData}
                             loadedPollingLocsData={loadedPollingLocsData} indicator={indicator} 
                             loadedStatesData={loadedStatesData} 
                             pctBlackCVAPCountyFilter={pctBlackCVAPCountyFilter} pctHispanicCVAPCountyFilter={pctHispanicCVAPCountyFilter} popDensityCountyFilter={popDensityCountyFilter}
                             pctBlackCVAPTractFilter={pctBlackCVAPTractFilter} pctHispanicCVAPTractFilter={pctHispanicCVAPTractFilter} popDensityTractFilter={popDensityTractFilter}
                             changeNoPollsCountyFilter={changeNoPollsCountyFilter} overallChangeCountyFilter={overallChangeCountyFilter}
                             changeYearCVAPPerPollCountyFilter={changeYearCVAPPerPollCountyFilter} baseYearCVAPPerPollCountyFilter={baseYearCVAPPerPollCountyFilter}
                             changeNoPollsTractFilter={changeNoPollsTractFilter} overallChangeTractFilter={overallChangeTractFilter}
                             setVisualizationLoading={setVisualizationLoading}
                             />
            <ZoomControl position="bottomright"/>
            <ScaleControl position ="bottomright" />
            <FullScreen mapRef={mapRef} geoFocus={geoFocus} setGeoFocus={setGeoFocus} selectedState={selectedState}
            setSelectedState={setSelectedState} setSelectedCounty={setSelectedCounty}/>
        </MapContainer>
    );
}
